"use client";

import { cva } from "class-variance-authority";
import { ChevronRight, KeyRound } from "lucide-react";
import * as React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/atoms/tooltip";
import { cn } from "@/lib/utils";
import { McpToolkitAvatar } from "./toolkit-avatar";

export type McpServerCardSelectionState = "none" | "partial" | "selected";

const shellVariants = cva(
  "flex items-stretch rounded-lg border transition-[background-color,border-color,box-shadow,transform] duration-[var(--duration-normal)] ease-[var(--ease-smooth)] motion-reduce:transition-colors",
  {
    variants: {
      selectionState: {
        selected:
          "border-foreground/45 bg-muted/20 shadow-[var(--shadow-card)]",
        partial:
          "border-foreground/45 border-dashed bg-muted/20 shadow-[var(--shadow-card)]",
        none: "border-border hover:border-foreground/40 hover:shadow-[var(--shadow-card-hover)]",
        "setup-none":
          "border-warning/15 hover:border-warning/50 hover:shadow-[var(--shadow-card-hover)]",
        "setup-selected":
          "border-warning/50 bg-warning/[0.5%] shadow-[var(--shadow-card)]",
        "setup-partial":
          "border-warning/50 border-dashed bg-warning/[0.5%] shadow-[var(--shadow-card)]",
      },
    },
    defaultVariants: { selectionState: "none" },
  }
);

function resolveShellVariant(
  selectionState: McpServerCardSelectionState,
  needsSetup: boolean
):
  | "selected"
  | "partial"
  | "none"
  | "setup-none"
  | "setup-selected"
  | "setup-partial" {
  if (!needsSetup) {
    return selectionState;
  }
  if (selectionState === "selected") {
    return "setup-selected";
  }
  if (selectionState === "partial") {
    return "setup-partial";
  }
  return "setup-none";
}

type McpServerCardBaseProps = {
  name: string;
  description?: string;
  /**
   * Toolkit id used to resolve the brand icon. Falls back to a letter avatar
   * when no brand mark is registered. The fallback is shared with
   * `McpToolkitChip` and the summary bar via `McpToolkitAvatar`.
   */
  toolkitId?: string;
  /**
   * Optional server version, shown muted next to the name (e.g. "v1.2.4").
   * Matches the `version` slot on `McpToolCard` so the two cards read as a
   * pair when stacked together.
   */
  version?: string;
  toolCount: number;
  selectedToolCount?: number;
  selectionState?: McpServerCardSelectionState;
  /**
   * Whether the server still needs configuration before its tools can run.
   * The card remains selectable regardless. Blocking selection based on setup
   * status is the consumer's responsibility, not the card's.
   */
  needsSetup?: boolean;
  /** Fired from the setup strip when `needsSetup` is true. */
  onSetup?: () => void;
} & Omit<React.ComponentProps<"div">, "onToggle">;

type McpServerCardToggleProps = McpServerCardBaseProps & {
  /**
   * How the card body behaves on click:
   * - `"toggle"` (default): the body adds/removes the whole server (`onToggle`),
   *   with a separate right-edge control to drill into individual tools.
   */
  interaction?: "toggle";
  onToggle: () => void;
  onCustomize: () => void;
};

type McpServerCardDrillProps = McpServerCardBaseProps & {
  /**
   * `"drill"`: the card has no whole-server toggle. Clicking it opens the
   * server's tool list via `onCustomize` so the user can pick a specific tool.
   *
   * Use this in single-tool contexts such as a tool-execution page, where the
   * goal is to select one tool to run rather than to add an entire server's
   * tools to a configuration. Selecting the server is not meaningful in that
   * context; the card is a navigation step toward the tool.
   */
  interaction: "drill";
  onCustomize: () => void;
  onToggle?: never;
};

export type McpServerCardProps =
  | McpServerCardToggleProps
  | McpServerCardDrillProps;

// Maps selectionState to aria-pressed. "mixed" is the ARIA tri-state value for
// partial selections and is valid per the aria spec. Clicking a "partial" card
// always adds all remaining tools (moves to "selected"), matching the label
// "Add all" that ToggleButton shows in that state.
function getTogglePressed(
  selectionState: McpServerCardSelectionState
): boolean | "mixed" {
  if (selectionState === "selected") {
    return true;
  }
  if (selectionState === "partial") {
    return "mixed";
  }
  return false;
}

// ─── private subcomponents ─────────────────────────────────────────────────

/**
 * Left-side affordance covering the whole card except the right-edge
 * customize strip. Clicking it adds or removes the whole server.
 */
function ToggleButton({
  name,
  description,
  toolkitId,
  version,
  selectionState,
  onToggle,
}: {
  name: string;
  description?: string;
  toolkitId?: string;
  version?: string;
  selectionState: McpServerCardSelectionState;
  onToggle?: () => void;
}) {
  const verb = selectionState === "selected" ? "Remove all" : "Add all";
  const ariaLabel = `${verb} ${name} tools`;

  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={getTogglePressed(selectionState)}
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-l-lg px-3 py-2.5 text-left transition-[background-color,color,transform] duration-[var(--duration-fast)] ease-[var(--ease-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset active:scale-[0.98] motion-reduce:transition-colors motion-reduce:active:scale-100"
      onClick={onToggle}
      title={description || undefined}
      type="button"
    >
      <McpToolkitAvatar name={name} size="md" toolkitId={toolkitId} />
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span className="min-w-0 truncate font-medium text-base capitalize">
          {name}
        </span>
        {version ? (
          <span className="shrink-0 font-normal text-muted-foreground text-xs tabular-nums">
            v{version}
          </span>
        ) : null}
      </span>
    </button>
  );
}

// DrillButton and CustomizeButton both fire onCustomize but serve different
// layout roles. DrillButton fills the full card width (no toggle alongside it)
// and includes the avatar, name, and tools count. CustomizeButton is a narrow
// right-side strip that sits next to ToggleButton. They cannot be the same
// component because their shape, content, and corner radii differ.

/**
 * Full-width button for drill mode. The card has no toggle, so the entire
 * surface opens the server's tool list. Includes the avatar, name, version,
 * and tool count so the card reads as a server entry point rather than a
 * selection target.
 */
function DrillButton({
  hasSetup,
  name,
  description,
  toolkitId,
  version,
  toolsLabel,
  onCustomize,
}: {
  hasSetup?: boolean;
  name: string;
  description?: string;
  toolkitId?: string;
  version?: string;
  toolsLabel: string;
  onCustomize?: () => void;
}) {
  return (
    <button
      aria-label={`Choose individual tools for ${name}`}
      className={cn(
        "flex min-w-0 flex-1 cursor-pointer items-center gap-2 px-3 py-2.5 text-left transition-[background-color,color,transform] duration-[var(--duration-fast)] ease-[var(--ease-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset active:scale-[0.98] motion-reduce:transition-colors motion-reduce:active:scale-100",
        hasSetup ? "rounded-l-lg" : "rounded-lg"
      )}
      onClick={onCustomize}
      title={description || undefined}
      type="button"
    >
      <McpToolkitAvatar name={name} size="md" toolkitId={toolkitId} />
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span className="min-w-0 truncate font-medium text-base capitalize">
          {name}
        </span>
        {version ? (
          <span className="shrink-0 font-normal text-muted-foreground text-xs tabular-nums">
            v{version}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground text-xs">
        <span className="whitespace-nowrap tabular-nums">{toolsLabel}</span>
        <ChevronRight
          aria-hidden
          className="size-3 transition-transform duration-[var(--duration-fast)] ease-[var(--ease-smooth)] motion-reduce:transition-none"
        />
      </span>
    </button>
  );
}

/**
 * Right-side affordance that opens the server's individual tool list.
 */
function CustomizeButton({
  name,
  toolsLabel,
  onCustomize,
}: {
  name: string;
  toolsLabel: string;
  onCustomize?: () => void;
}) {
  return (
    <button
      aria-label={`Choose individual tools for ${name}`}
      className="flex min-w-[5.5rem] shrink-0 cursor-pointer items-center justify-end gap-1.5 rounded-r-lg border-border/60 border-l px-2.5 text-muted-foreground text-xs transition-[background-color,color,transform] duration-[var(--duration-fast)] ease-[var(--ease-smooth)] hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset active:scale-[0.98] motion-reduce:transition-colors motion-reduce:active:scale-100"
      data-slot="mcp-server-card-customize"
      onClick={onCustomize}
      type="button"
    >
      <span className="whitespace-nowrap tabular-nums">{toolsLabel}</span>
      <ChevronRight
        aria-hidden
        className="size-3 transition-transform duration-[var(--duration-fast)] ease-[var(--ease-smooth)] motion-reduce:transition-none"
      />
    </button>
  );
}

/**
 * Compact full-height setup button for a server that still needs configuration.
 * It is separate from selection/customize controls, so callers can let users
 * select the server first and finish setup from this strip.
 */
function SetupButton({
  isTerminal = false,
  name,
  onSetup,
}: {
  isTerminal?: boolean;
  name: string;
  onSetup?: () => void;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              aria-label={`Set up ${name}`}
              className={cn(
                "flex shrink-0 cursor-pointer items-center justify-center border-border/60 border-l bg-warning/10 px-2.5 text-warning transition-colors duration-[var(--duration-fast)] ease-[var(--ease-smooth)] hover:bg-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset",
                isTerminal ? "rounded-r-lg" : ""
              )}
              data-slot="mcp-server-card-setup"
              onClick={onSetup}
              type="button"
            >
              <KeyRound aria-hidden className="size-4" />
            </button>
          }
        />
        <TooltipContent
          className="max-w-56 flex-col items-start gap-0.5 text-left"
          side="top"
        >
          <span className="font-semibold">Setup required</span>
          <span>
            This server isn't ready yet. It needs some configuration before its
            tools work.
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── exported component ────────────────────────────────────────────────────

function McpServerCardImpl({
  name,
  description,
  toolkitId,
  version,
  toolCount,
  selectedToolCount = 0,
  selectionState = "none",
  needsSetup = false,
  interaction = "toggle",
  onSetup,
  onToggle,
  onCustomize,
  className,
  ...rest
}: McpServerCardProps) {
  const toolsLabel =
    selectionState === "partial"
      ? `${selectedToolCount}/${toolCount} tools`
      : `${toolCount} ${toolCount === 1 ? "tool" : "tools"}`;

  return (
    <div
      {...rest}
      className={cn(
        shellVariants({
          selectionState: resolveShellVariant(selectionState, needsSetup),
        }),
        className
      )}
      data-slot="mcp-server-card"
      data-status={needsSetup ? "needs-setup" : selectionState}
    >
      {interaction === "drill" ? (
        <>
          <DrillButton
            description={description}
            hasSetup={needsSetup}
            name={name}
            onCustomize={onCustomize}
            toolkitId={toolkitId}
            toolsLabel={toolsLabel}
            version={version}
          />
          {needsSetup ? (
            <SetupButton isTerminal name={name} onSetup={onSetup} />
          ) : null}
        </>
      ) : (
        <>
          <ToggleButton
            description={description}
            name={name}
            onToggle={onToggle}
            selectionState={selectionState}
            toolkitId={toolkitId}
            version={version}
          />
          {needsSetup ? <SetupButton name={name} onSetup={onSetup} /> : null}
          <CustomizeButton
            name={name}
            onCustomize={onCustomize}
            toolsLabel={toolsLabel}
          />
        </>
      )}
    </div>
  );
}

export const McpServerCard = React.memo(McpServerCardImpl);
export { shellVariants as mcpServerCardShellVariants };
